import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { formatMoney, formatNumber, sourceLabel } from "@/lib/api";
import { Calculator } from "lucide-react";

export default function Valuation() {
  const [params] = useSearchParams();
  const [form, setForm] = useState({
    make: params.get("make") || "",
    model_name: params.get("model_name") || params.get("model") || "",
    year: params.get("year") || "",
    hours: params.get("hours") || "",
    km: "",
    klass: params.get("klass") || "",
  });
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (form.make && form.model_name) submit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function submit(e) {
    if (e) e.preventDefault();
    setBusy(true); setErr("");
    try {
      const payload = {
        ...form,
        year: form.year ? Number(form.year) : null,
        hours: form.hours ? Number(form.hours) : null,
        km: form.km ? Number(form.km) : null,
      };
      const { data } = await api.post("/valuation", payload);
      setResult(data);
    } catch (e2) {
      setErr(e2.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-6" data-testid="valuation-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-extrabold text-[color:var(--caa-charcoal)]">
          Instant crane valuation
        </h1>
        <p className="text-sm text-[color:var(--caa-muted)] mt-1">
          Comparable-crane valuation from every open and closed lot in the CAA database. Open-auction bids are flagged and weighted lower than confirmed sales.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="caa-card p-6 space-y-4">
          <form onSubmit={submit} className="space-y-3" data-testid="valuation-form">
            <TextField label="Make" testId="v-make" value={form.make}
              onChange={(v) => setForm({ ...form, make: v })} placeholder="e.g. Franna" />
            <TextField label="Model" testId="v-model" value={form.model_name}
              onChange={(v) => setForm({ ...form, model_name: v })} placeholder="AT-22" />
            <div className="grid grid-cols-2 gap-3">
              <TextField label="Year" testId="v-year" value={form.year}
                onChange={(v) => setForm({ ...form, year: v })} type="number" />
              <TextField label="Hours" testId="v-hours" value={form.hours}
                onChange={(v) => setForm({ ...form, hours: v })} type="number" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <TextField label="Kilometres" testId="v-km" value={form.km}
                onChange={(v) => setForm({ ...form, km: v })} type="number" />
              <TextField label="Class" testId="v-klass" value={form.klass}
                onChange={(v) => setForm({ ...form, klass: v })} placeholder="Pick & Carry" />
            </div>
            {err && <div className="text-sm text-[color:var(--caa-status-passed)]">{err}</div>}
            <button disabled={busy} data-testid="valuation-submit" type="submit"
              className="w-full inline-flex items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-semibold text-white disabled:opacity-70"
              style={{ background: "var(--caa-teal-deep)" }}>
              <Calculator size={15} /> {busy ? "Calculating…" : "Value this crane"}
            </button>
          </form>
        </div>

        <div className="lg:col-span-2 space-y-4">
          {result ? (
            <>
              <div className="caa-card p-6" data-testid="valuation-estimate">
                <div className="text-xs uppercase tracking-wider text-[color:var(--caa-muted)] font-semibold">
                  Estimated value range
                </div>
                {result.estimate_mid ? (
                  <div className="flex items-baseline gap-3 mt-2 flex-wrap">
                    <div className="text-3xl font-extrabold caa-num" style={{ color: "var(--caa-teal-deep)" }} data-testid="est-mid">
                      {formatMoney(result.estimate_mid)}
                    </div>
                    <div className="text-sm text-[color:var(--caa-muted)] caa-num">
                      (<span data-testid="est-low">{formatMoney(result.estimate_low)}</span> – <span data-testid="est-high">{formatMoney(result.estimate_high)}</span>)
                    </div>
                  </div>
                ) : (
                  <div className="text-lg font-semibold text-[color:var(--caa-muted)] mt-2" data-testid="est-mid">
                    Not enough data
                  </div>
                )}
                <p className="text-sm text-[color:var(--caa-charcoal)] mt-3" data-testid="est-confidence">{result.confidence}</p>
              </div>

              <div className="caa-card p-6" data-testid="valuation-comparables">
                <h3 className="font-bold text-[color:var(--caa-charcoal)] mb-3">
                  Closest comparables ({result.comparables.length})
                </h3>
                {result.comparables.length === 0 ? (
                  <p className="text-sm text-[color:var(--caa-muted)]">
                    No comparable machines matched — try broader criteria (drop the class or year).
                  </p>
                ) : (
                  <ul className="divide-y caa-hairline">
                    {result.comparables.map((c) => (
                      <li key={c.lot_id} className="py-3 flex items-start justify-between gap-4">
                        <div className="min-w-0">
                          <Link to={`/lots/${c.lot_id}`}
                            className="text-sm font-semibold text-[color:var(--caa-charcoal)] hover:text-[color:var(--caa-teal-deep)]">
                            {c.title}
                          </Link>
                          <div className="text-xs text-[color:var(--caa-muted)] mt-0.5">
                            {sourceLabel(c.source)} · {c.year || "—"} · {formatNumber(c.hours)} hrs · {c.klass}
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <div className="text-sm font-bold caa-num" style={{ color: "var(--caa-teal-deep)" }}>
                            {formatMoney(c.price)}
                          </div>
                          <div className="flex items-center justify-end gap-1.5 mt-0.5">
                            <PriceTypePill type={c.price_type} />
                            <span className="text-xs font-semibold" style={{ color: "var(--caa-teal-deep)" }}>
                              {c.match_pct}% match
                            </span>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </>
          ) : (
            <div className="caa-card p-10 text-center">
              <div className="w-12 h-12 rounded-xl mx-auto flex items-center justify-center mb-3"
                style={{ background: "var(--caa-teal-soft)", color: "var(--caa-teal-deep)" }}>
                <Calculator size={22} />
              </div>
              <p className="text-sm text-[color:var(--caa-muted)]">
                Fill in the machine details and press{" "}
                <span className="font-semibold text-[color:var(--caa-charcoal)]">Value this crane</span>{" "}
                — we&apos;ll pull the closest comparables from our database, tagged as confirmed sales or live open bids.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function PriceTypePill({ type }) {
  if (type === "final") {
    return (
      <span data-testid="price-type-final"
        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider"
        style={{ background: "var(--caa-status-live-bg)", color: "var(--caa-status-live)" }}>
        Sold
      </span>
    );
  }
  if (type === "current") {
    return (
      <span data-testid="price-type-current"
        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider"
        style={{ background: "#FDECD3", color: "#A9540B" }}>
        Open bid
      </span>
    );
  }
  // Unknown / missing price_type — render nothing rather than falsely tag as OPEN.
  return null;
}

function TextField({ label, testId, value, onChange, placeholder, type = "text" }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-wider font-semibold text-[color:var(--caa-muted)]">{label}</label>
      <input data-testid={testId} type={type} value={value}
        onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        className="mt-1 w-full rounded-lg border caa-hairline bg-white px-3 py-2 text-sm outline-none focus:border-[color:var(--caa-teal-deep)]" />
    </div>
  );
}
